import { useEffect, useState, useRef } from "react";
import * as React from "react";
import { Form, Button, FormInstance } from "antd";

import CompanyIdentificationForm from "./CompanyIdentificationForm";
import FormulaBuilderForm from "../FormulaBuilderForm";
import { validateForm } from "../../../utils/validation";
import { buildFormula, FormulaType } from "../../../utils/formulaBuilder";
import { CompanyIdentificationType, CompanyIdentificationValue, CompanyPlaceholderName, CurrencySelectionType, CurrencyValue } from "../../../types/newLinkBuilder";
import NewLinkBuilerDataSelection from "./DataSelection";
import { DialogMessageType } from "../../../types/commands";
import { useStyles } from "../../style";
import CurrencyForm from "./CurrencyForm";

const NewLinkBuilderForm: React.FC = () => {
    const styles = useStyles();
    const [form] = Form.useForm();

    const companyIdentificationRef = useRef<HTMLDivElement>(null);
    const formulaBuilderRef = useRef<HTMLDivElement>(null);
    const currencyRef = useRef<HTMLDivElement>(null);
    const [scrollTarget, setScrollTarget] = useState<'company' | 'formula' | 'currency' | null>(null);

    const [selectedDataPoint, setSelectedDataPoint] = useState<string | null>('Legal_Name');

    const [companyIdentification, setCompanyIdentification] = useState<CompanyIdentificationValue>({
        selected: CompanyIdentificationType.Search,
        searchInput: "",
        exchange: "",
        ticker: "",
    });

    const [currency, setCurrency] = useState<CurrencyValue>({
        selected: CurrencySelectionType.Value,
        currency: "Trading_Currency",
        currencyCellReference: "",
    });

    const [showCurrencyForm, setShowCurrencyForm] = useState<boolean>(false);

    const [formulaLocation, setFormulaLocation] = useState<string>("");
    const [formula, setFormula] = useState<string>("");

    useEffect(() => {
        form.setFieldsValue({
            companyIdentification: companyIdentification.selected,
            currencyType: currency.selected 
        });
    }, [companyIdentification.selected, currency]);

    useEffect(() => {
        const isCurrencyUsed = ['IPO_Offering_Price'].includes(selectedDataPoint)
        setShowCurrencyForm(isCurrencyUsed)

        const buildFormulaAsync = async () => {
            const formula = await buildFormula(
                FormulaType.Company,
                {
                    selectedDataPoint,
                    companyIdentification,
                    currency,
                    isCurrencyUsed

                }
            );
            setFormula(formula);
        };
        buildFormulaAsync();
    }, [selectedDataPoint, companyIdentification]);

    useEffect(() => {
        if (scrollTarget === 'company') {
            companyIdentificationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        } else if (scrollTarget === 'formula') {
            formulaBuilderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        } else if (scrollTarget === 'currency') {
            currencyRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [scrollTarget]);

    useEffect(() => {
        const placeholder = localStorage.getItem("placeholder");
        const cellAddress = localStorage.getItem("cellAddress");
        const activeCell = localStorage.getItem("activeCell");

        if (activeCell) {
            setFormulaLocation(activeCell);
        }

        if (placeholder) {
            const placeholderData = JSON.parse(placeholder);

            setSelectedDataPoint(placeholderData.data.selectedDataPoint);
            setCompanyIdentification(placeholderData.data.companyIdentification);
            setFormulaLocation(placeholderData.data.formulaLocation);
            setShowCurrencyForm(placeholderData.data.showCurrencyForm);

            switch (placeholderData.placeholderName) {
                case CompanyPlaceholderName.CompanyIdentificationExchangeCellReference:
                    setCompanyIdentification(prev => ({
                        ...prev,
                        selected: CompanyIdentificationType.CellReference,
                        exchange: cellAddress,
                    }));
                    setScrollTarget('company');
                    break;
                case CompanyPlaceholderName.CompanyIdentificationTickerCellReference:
                    setCompanyIdentification(prev => ({
                        ...prev,
                        selected: CompanyIdentificationType.CellReference,
                        ticker: cellAddress,
                    }));
                    setScrollTarget('company');
                    break;
                case CompanyPlaceholderName.FormulaLocationCellReference:
                    setFormulaLocation(cellAddress);
                    setScrollTarget('formula');
                    break;
                case CompanyPlaceholderName.CurrencyCellReference:
                    setCurrency({
                        ...placeholderData.data.currency,
                        currencyCellReference: cellAddress
                    });
                    setScrollTarget('currency');
                    break;

                default:
                    break;
            }
        }

        return () => {
            localStorage.removeItem("placeholder");
            localStorage.removeItem("cellAddress");
        }
    }, []);

    const handleOk = (form: FormInstance) => {
        const companyIdentificationError = validateForm(companyIdentification, form, "companyIdentification");
        const formulaLocationError = validateForm(formulaLocation, form, "formulaLocation");

        const hasError = companyIdentificationError || formulaLocationError;

        if (!hasError) {
            Office.context.ui.messageParent(JSON.stringify({
                action: DialogMessageType.ADD_LINK_TO_CELL,
                data: {
                    formula: formula,
                    address: formulaLocation,
                },
            }));
        }
    }

    const handleCancel = () => {
        Office.context.ui.messageParent(JSON.stringify({
            action: DialogMessageType.CLOSE,
        }));
    }

    const handlePlaceholder = (name: CompanyPlaceholderName) => {
        const value = {
            placeholderName: name,
            data: {
                selectedDataPoint,
                companyIdentification,
                formulaLocation,
                currency,
                showCurrencyForm
            }
        }

        console.log(value)

        localStorage.setItem("placeholder", JSON.stringify(value));
        localStorage.setItem("redirectTo", "company-data");
        Office.context.ui.messageParent(JSON.stringify({
            action: DialogMessageType.OPEN_PLACEHOLDER,
        }));
    }

    return (
        <Form form={form}
            initialValues={{
                companyIdentification: CompanyIdentificationType.Search,
                currencyType: CurrencySelectionType.Value,
            }}
            onValuesChange={(changedValues) => {
                if (changedValues.companyIdentification !== undefined) {
                    setCompanyIdentification(prev => ({
                        ...prev,
                        selected: changedValues.companyIdentification,
                        ...(changedValues.companyIdentification === CompanyIdentificationType.Search
                            ? { exchange: "", ticker: "" }
                            : { searchInput: "" })
                    }));
                }

                if (changedValues.currencyType !== undefined) {
                    setCurrency(prev => ({
                        ...prev,
                        selected: changedValues.currencyType,
                        ...(changedValues.currencyType === CurrencySelectionType.Value
                            ? { currencyCellReference: "", currency: "Trading_Currency" }
                            : { currency: "" })
                    }));
                }
            }}
            className={styles.radio}
        >
            <NewLinkBuilerDataSelection
                selectedDataPoint={selectedDataPoint}
                setSelectedDataPoint={setSelectedDataPoint}
            />
            <div ref={companyIdentificationRef}>
                <CompanyIdentificationForm value={companyIdentification} setValue={setCompanyIdentification} handlePlaceholder={handlePlaceholder} />
            </div>

            {showCurrencyForm?
                <div ref={currencyRef}>
                    <CurrencyForm currencyValue={currency} setCurrencyValue={setCurrency} handlePlaceholder={handlePlaceholder} />
                </div>
            : null
            }

            <div ref={formulaBuilderRef}>
                <FormulaBuilderForm formula={formula} formulaLocation={formulaLocation} setFormulaLocation={setFormulaLocation} handlePlaceholder={handlePlaceholder} />
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '10px',
                marginBlock: '20px',
            }}>
                <Button type="primary" onClick={() => handleOk(form)}>Ok</Button>
                <Button onClick={handleCancel}>Cancel</Button>
            </div>
        </Form>
    );
};

export default NewLinkBuilderForm;
